import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QuestionInput } from "../../components/QuestionInput";
import { UserChatMessage } from "../../components/UserChatMessage";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import { DataScopePicker } from "../workflowData/DataScopePicker";
import { hasDataScope, scopeProjectId, type DataScope } from "../workflowData/models";
import { DocumentationWorkspace } from "../documentation/DocumentationWorkspace";
import { useBuilderIdentity } from "../workflowBuilder/useBuilderIdentity";
import { executePersonal } from "./api";
import { PersonalRunCard } from "./PersonalRunCard";
import type { HomeFlow, PersonalRun, RunRequest } from "./models";
import styles from "./HomeWorkflows.module.css";

export function HomeWorkflowFlow({ flow, onClose, composerTarget }: { flow: HomeFlow; onClose: () => void; composerTarget: HTMLElement | null }) {
    const { t, i18n } = useTranslation();
    const [chosen, setChosen] = useState(flow.route.action === "start" ? flow.route.candidates[0] : null);
    const [dataScope, setDataScope] = useState<DataScope | null>(flow.route.candidates[0]?.data_scope ?? null);
    const projectId = scopeProjectId(dataScope);
    const [run, setRun] = useState<PersonalRun | null>(null);
    const [followups, setFollowups] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const pending = useRef<RunRequest | null>(null);
    const running = useRef(false);
    const initialStarted = useRef(false);
    const controller = useRef<AbortController | null>(null);
    const mounted = useRef(false);
    const { resolveToken } = useBuilderIdentity();
    const text = (key: string, fallback: string) => t(`homeWorkflow.${key}`, { defaultValue: fallback });
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            controller.current?.abort();
        };
    }, []);
    const execute = useCallback(
        async (input: string, retry = false) => {
            if (!chosen || running.current) return;
            running.current = true;
            setBusy(true);
            setError("");
            const abort = new AbortController();
            controller.current = abort;
            const timeout = setTimeout(() => abort.abort(), 600_000);
            const request =
                retry && pending.current
                    ? pending.current
                    : {
                          request_id: crypto.randomUUID(),
                          workflow_id: chosen.id,
                          message: input,
                          language: i18n.language,
                          project_id: projectId, data_scope: dataScope,
                          ...(run ? { run_id: run.id, expected_revision: run.revision } : {})
                      };
            if (!retry && request.run_id) setFollowups(current => [...current, request.message]);
            pending.current = request;
            try {
                const result = await executePersonal(request, await resolveToken(), abort.signal);
                if (!abort.signal.aborted) {
                    setRun(result);
                    pending.current = null;
                }
            } catch (cause) {
                if (!abort.signal.aborted) setError(cause instanceof Error ? cause.message : String(cause));
                else if (mounted.current && controller.current === abort)
                    setError("The request timed out. Your input is preserved; retry to check the same run.");
            } finally {
                clearTimeout(timeout);
                running.current = false;
                if (mounted.current && controller.current === abort) setBusy(false);
            }
        },
        [chosen, i18n.language, projectId, dataScope, resolveToken, run]
    );
    useEffect(() => {
        if (!chosen || chosen.kind !== "personal" || (chosen.requires_project && !hasDataScope(dataScope, projectId)) || initialStarted.current) return;
        // Defer until the effect is still mounted; StrictMode cleans up its first setup.
        let cancelled = false;
        void Promise.resolve().then(() => {
            if (cancelled || initialStarted.current) return;
            initialStarted.current = true;
            void execute(flow.message);
        });
        return () => {
            cancelled = true;
        };
    }, [chosen, projectId, dataScope, flow.message, execute]);
    return (
        <section className={styles.inlineFlow} aria-label={chosen?.title ?? text("choose", "Choose a workflow")}>
            <UserChatMessage message={flow.message} />
            <div className={styles.flowHeading}>
                <span>{chosen?.title ?? text("choose", "Choose a workflow")}</span>
                <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
                    {text("backToChat", "Back to chat")}
                </Button>
            </div>
            <div className={styles.body}>
                {!chosen && (
                    <div className={styles.choices}>
                        <p>{flow.route.question}</p>
                        {flow.route.candidates.map(candidate => (
                            <Button key={`${candidate.kind}:${candidate.id}`} onClick={() => { setChosen(candidate); setDataScope(candidate.data_scope ?? null); }}>
                                {candidate.title} · {candidate.kind === "admin" ? "Admin" : "Personal"}
                            </Button>
                        ))}
                    </div>
                )}
                {chosen?.kind === "admin" && (
                    <DocumentationWorkspace
                        onDismiss={onClose}
                        initialMessage={flow.message}
                        routedWorkflowId={chosen.id}
                        projectId={flow.route.project_id ?? undefined}
                        embedded
                        composerTarget={composerTarget}
                    />
                )}
                {chosen?.kind === "personal" && (
                    <>
                        {followups.map((message, index) => (
                            <UserChatMessage key={index} message={message} />
                        ))}
                        {chosen.requires_project && !initialStarted.current && (
                            <DataScopePicker value={dataScope} disabled={busy} onChange={setDataScope} />
                        )}
                        {busy && <p role="status">{text("running", "Running workflow…")}</p>}
                        {error && (
                            <div role="alert">
                                <p>{error}</p>
                                <Button disabled={busy} onClick={() => void execute(flow.message, true)}>
                                    {text("retry", "Retry")}
                                </Button>
                            </div>
                        )}
                        {run && (
                            <>
                                <PersonalRunCard run={run} />
                                <p>
                                    {text("saved", "Saved to your private Workspace.")} <Link to="/app/workspace">{text("workspace", "Open Workspace")}</Link>
                                </p>
                            </>
                        )}
                    </>
                )}
            </div>
            {chosen?.kind !== "admin" &&
                composerTarget &&
                createPortal(
                    <QuestionInput
                        clearOnSend
                        placeholder={text("followup", "Add details or request changes to this card")}
                        disabled={busy || !run || Boolean(error)}
                        onSend={message => {
                            if (message.trim()) void execute(message);
                        }}
                        showSpeechInput={Boolean(run) && !busy}
                    />,
                    composerTarget
                )}
        </section>
    );
}
